import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Database, ChefHat, DollarSign, Calculator,
  Percent, Upload, FileText, ArrowRight, Pizza, TrendingUp,
  GitCompareArrows, Sparkles, Save, FolderOpen, LogIn, UserPlus, CheckCircle
} from 'lucide-react';

const formatCurrency = (val) => `$${(val || 0).toFixed(2)}`;

export default function Dashboard() {
  const navigate = useNavigate();
  const { ingredients, recipes, breakEvenData, pnlData, specials, loadDemoData, setIngredients, setRecipes, setBreakEvenData, setPnlData } = useApp();
  const { user, saveData, loadData, logout } = useAuth();
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    const ok = await saveData({ ingredients, recipes, breakEvenData, pnlData, specials });
    setSaveMsg(ok ? 'Saved!' : 'Save failed');
    setSaving(false);
    if (ok) setTimeout(() => setSaveMsg(''), 3000);
  };

  const handleLoad = async () => {
    setLoadingData(true);
    const data = await loadData();
    if (data) {
      if (data.ingredients) setIngredients(data.ingredients);
      if (data.recipes) setRecipes(data.recipes);
      if (data.breakEvenData) setBreakEvenData(data.breakEvenData);
      if (data.pnlData) setPnlData(data.pnlData);
      setSaveMsg('Data loaded!');
      setTimeout(() => setSaveMsg(''), 3000);
    } else {
      setSaveMsg('No saved data found');
      setTimeout(() => setSaveMsg(''), 3000);
    }
    setLoadingData(false);
  };

  // Quick stats
  const totalIngredients = ingredients.length;
  const totalRecipes = recipes.length;
  const avgFoodCost = recipes.length > 0
    ? recipes.reduce((sum, r) => sum + (r.foodCostPercent || 0), 0) / recipes.length
    : 0;

  const totalFixedCosts = Object.entries(breakEvenData)
    .filter(([k]) => !['avgOrderValue', 'avgContributionMargin'].includes(k))
    .reduce((sum, [, v]) => sum + (Number(v) || 0), 0);

  const totalRevenue = Number(pnlData.revenue) || 0;
  const totalExpenses = Object.entries(pnlData)
    .filter(([k]) => k !== 'revenue')
    .reduce((sum, [, v]) => sum + (Number(v) || 0), 0);
  const netProfit = totalRevenue - totalExpenses;

  const QUICK_ACTIONS = [
    { label: 'Add Ingredients', icon: Database, path: '/ingredients', color: '#E23D28', desc: `${totalIngredients} items` },
    { label: 'Build Recipes', icon: ChefHat, path: '/recipes', color: '#FFC107', desc: `${totalRecipes} recipes` },
    { label: 'Compare Recipes', icon: GitCompareArrows, path: '/compare', color: '#2E7D32', desc: 'Side by side' },
    { label: 'Labor Costs', icon: DollarSign, path: '/labor', color: '#2E7D32', desc: 'Calculate labor' },
    { label: 'Break-Even', icon: Calculator, path: '/break-even', color: '#E23D28', desc: 'Find your number' },
    { label: 'Specials', icon: Percent, path: '/specials', color: '#FFC107', desc: `${specials.length} specials` },
    { label: 'Upload Data', icon: Upload, path: '/upload', color: '#2E7D32', desc: 'CSV / XLSX' },
    { label: 'Reports', icon: FileText, path: '/reports', color: '#E23D28', desc: 'P&L Snapshot' },
  ];

  return (
    <div className="page-enter p-4 md:p-8 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-[#FFC107] rounded-full border-2 border-[#2A2A2A] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(42,42,42,1)]">
            <Pizza className="w-5 h-5 text-[#2A2A2A]" />
          </div>
          <h1
            className="text-4xl sm:text-5xl text-[#2A2A2A]"
            style={{ fontFamily: "'Bebas Neue', cursive" }}
            data-testid="dashboard-title"
          >
            Dashboard
          </h1>
        </div>
        <p className="text-[#2A2A2A]/60 text-sm ml-[52px]" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Your restaurant numbers at a glance.
        </p>
      </div>

      {/* Auth & Save/Load Bar */}
      {user ? (
        <div className="pizza-card p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="dashboard-user-bar">
          <div className="flex items-center gap-2 text-sm">
            <div className="w-8 h-8 bg-[#2E7D32] rounded-full flex items-center justify-center text-white font-bold text-xs">
              {user.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="font-medium text-[#2A2A2A]">{user.name || user.email}</p>
              <p className="text-xs text-[#2A2A2A]/40">{user.businessName || user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {saveMsg && (
              <span className="text-xs text-[#2E7D32] flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {saveMsg}</span>
            )}
            <button onClick={handleSave} disabled={saving} className="pizza-btn text-xs px-4 py-2 flex items-center gap-1" data-testid="dashboard-save-btn">
              <Save className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save Data'}
            </button>
            <button onClick={handleLoad} disabled={loadingData} className="pizza-btn-secondary text-xs px-4 py-2 flex items-center gap-1" data-testid="dashboard-load-btn">
              <FolderOpen className="w-3.5 h-3.5" /> {loadingData ? 'Loading...' : 'Load Saved'}
            </button>
            <button onClick={logout} className="text-xs text-[#2A2A2A]/40 hover:text-[#E23D28] underline" data-testid="dashboard-logout-btn">
              Sign out
            </button>
          </div>
        </div>
      ) : user === false ? (
        <div className="pizza-card p-4 mb-6 flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="dashboard-auth-prompt">
          <p className="text-sm text-[#2A2A2A]/60">Create a free account to save your data and come back anytime.</p>
          <div className="flex gap-2">
            <button onClick={() => navigate('/register')} className="pizza-btn text-xs px-4 py-2 flex items-center gap-1" data-testid="dashboard-register-btn">
              <UserPlus className="w-3.5 h-3.5" /> Create Account
            </button>
            <button onClick={() => navigate('/login')} className="pizza-btn-secondary text-xs px-4 py-2 flex items-center gap-1" data-testid="dashboard-login-btn">
              <LogIn className="w-3.5 h-3.5" /> Sign In
            </button>
          </div>
        </div>
      ) : null}

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="dashboard-stats">
        <div className="stat-card">
          <p className="text-xs uppercase tracking-wider text-[#2A2A2A]/50 mb-1" style={{ fontFamily: "'Bebas Neue', cursive" }}>Ingredients</p>
          <p className="text-3xl font-bold text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{totalIngredients}</p>
          <p className="text-xs text-[#2A2A2A]/40">in database</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-wider text-[#2A2A2A]/50 mb-1" style={{ fontFamily: "'Bebas Neue', cursive" }}>Recipes</p>
          <p className="text-3xl font-bold text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{totalRecipes}</p>
          <p className="text-xs text-[#2A2A2A]/40">built</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-wider text-[#2A2A2A]/50 mb-1" style={{ fontFamily: "'Bebas Neue', cursive" }}>Avg Food Cost</p>
          <p className="text-3xl font-bold text-[#E23D28]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{avgFoodCost.toFixed(1)}%</p>
          <p className="text-xs text-[#2A2A2A]/40">across recipes</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-wider text-[#2A2A2A]/50 mb-1" style={{ fontFamily: "'Bebas Neue', cursive" }}>Net P&L</p>
          <p className={`text-3xl font-bold ${netProfit >= 0 ? 'text-[#2E7D32]' : 'text-[#E23D28]'}`} style={{ fontFamily: "'Bebas Neue', cursive" }}>
            {formatCurrency(netProfit)}
          </p>
          <p className="text-xs text-[#2A2A2A]/40">this month</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2
          className="text-2xl text-[#2A2A2A] mb-4"
          style={{ fontFamily: "'Bebas Neue', cursive" }}
        >
          Quick Actions
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="dashboard-actions">
          {QUICK_ACTIONS.map(({ label, icon: Icon, path, color, desc }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className="pizza-card p-4 text-left group cursor-pointer"
              data-testid={`action-${path.slice(1)}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div
                  className="w-9 h-9 rounded-lg border-2 border-[#2A2A2A] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(42,42,42,1)]"
                  style={{ background: color }}
                >
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <ArrowRight className="w-4 h-4 text-[#2A2A2A]/30 group-hover:text-[#E23D28] transition-colors" />
              </div>
              <h3 className="text-lg text-[#2A2A2A] mb-0.5" style={{ fontFamily: "'Bebas Neue', cursive" }}>{label}</h3>
              <p className="text-xs text-[#2A2A2A]/50">{desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Recent Recipes */}
      {recipes.length > 0 && (
        <div>
          <h2 className="text-2xl text-[#2A2A2A] mb-4" style={{ fontFamily: "'Bebas Neue', cursive" }}>
            Recent Recipes
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dashboard-recent-recipes">
            {recipes.slice(-6).reverse().map((recipe) => (
              <div key={recipe.id} className="pizza-card p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{recipe.name}</h3>
                  <span className="pizza-tag pizza-tag-red">{recipe.category || 'Uncategorized'}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-[#2A2A2A]/50 text-xs">Menu Price</p>
                    <p className="font-semibold text-[#2A2A2A]">{formatCurrency(recipe.menuPrice)}</p>
                  </div>
                  <div>
                    <p className="text-[#2A2A2A]/50 text-xs">Total Cost</p>
                    <p className="font-semibold text-[#E23D28]">{formatCurrency(recipe.totalCost)}</p>
                  </div>
                  <div>
                    <p className="text-[#2A2A2A]/50 text-xs">Gross Profit</p>
                    <p className="font-semibold text-[#2E7D32]">{formatCurrency(recipe.grossProfit)}</p>
                  </div>
                  <div>
                    <p className="text-[#2A2A2A]/50 text-xs">Food Cost %</p>
                    <p className="font-semibold text-[#2A2A2A]">{(recipe.foodCostPercent || 0).toFixed(1)}%</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {recipes.length === 0 && ingredients.length === 0 && (
        <div className="pizza-card p-8 text-center" data-testid="dashboard-empty-state">
          <div className="w-16 h-16 bg-[#FFC107] rounded-full border-2 border-[#2A2A2A] flex items-center justify-center mx-auto mb-4 shadow-[4px_4px_0px_0px_rgba(42,42,42,1)]">
            <Pizza className="w-8 h-8 text-[#2A2A2A]" />
          </div>
          <h3 className="text-2xl text-[#2A2A2A] mb-2" style={{ fontFamily: "'Bebas Neue', cursive" }}>
            Let's Get Cooking!
          </h3>
          <p className="text-[#2A2A2A]/60 text-sm mb-6 max-w-md mx-auto">
            Start by adding your ingredients or uploading a spreadsheet. Or load our demo pizza shop to see how it all works.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button onClick={() => navigate('/wizard')} className="pizza-btn text-sm flex items-center gap-2" data-testid="empty-guided-setup-btn">
              <Sparkles className="w-4 h-4" /> Guided Setup (5 min)
            </button>
            <button onClick={() => navigate('/ingredients')} className="pizza-btn-secondary text-sm" data-testid="empty-add-ingredients-btn">
              Add Ingredients
            </button>
            <button
              onClick={loadDemoData}
              className="bg-[#2E7D32] text-white border-2 border-[#2A2A2A] rounded-full px-6 py-3 font-bold uppercase tracking-widest shadow-[4px_4px_0px_0px_rgba(42,42,42,1)] hover:bg-[#256025] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(42,42,42,1)] transition-all text-sm flex items-center gap-2"
              style={{ fontFamily: "'Bebas Neue', cursive", letterSpacing: '0.1em' }}
              data-testid="load-demo-data-btn"
            >
              Load Demo Data
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
